import emailjs from '@emailjs/browser';
import { orderData, setCustomerOrderData } from '../orderData';
import { notify } from '../../toastify/toastify';
import { resetTripData } from '../step1/trip-form';
import { resetSelectedCars } from '../step2/selectedCars';
import { resetInfoForDriver } from './infoForDriver';
import { API_createOrder } from '../../../../api/orders/API_createOrder';
import { resetReturnTripData } from '../step1/returnTrip';
import {
  hideRedirectLoader,
  redirectToThankYou,
  showRedirectLoader,
} from '../../../utils/thankYouRedirect';
import { isValidPhone } from './utils/isValidPhone.js';
import { API_createCustomer } from '../../../../api/customers/API_createCustomer.js';
import { setCustomerData } from './customer-form.js';
import { common } from '../../../i18n/common';

emailjs.init(import.meta.env.VITE_EMAILJS_PUBLIC_KEY); // актуальний публічний ключ

// =====================================
// Селектори
// =====================================
const refs = {
  buttonSubmit: document.querySelector('[data-submit]'),
  confirmPaymentCheckbox: document.getElementById('confirm-payment'),
  // removed unused selectors (sectionMapForm, sectionPayment) to satisfy lint
  form: document.querySelector('.form-customer'),
};
const { buttonSubmit, confirmPaymentCheckbox, form } = refs;

const MIN_LOADER_DURATION = 2500;
function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function showErrorModal(message) {
  if (document.getElementById('order-error-modal')) return;

  const template = document.getElementById('order-error-modal-template');
  if (!template || !template.content) {
    alert(message || 'Something went wrong. Please try again.');
    return;
  }

  const modalWrapper = document.createElement('div');
  modalWrapper.id = 'order-error-modal';
  modalWrapper.className = 'global-error-modal';

  const clone = template.content.cloneNode(true);
  const messageEl = clone.querySelector('[data-modal-message]');
  if (messageEl) {
    messageEl.textContent =
      message ||
      messageEl.textContent ||
      'Something went wrong. Please try again.';
  }

  const close = () => modalWrapper.remove();
  clone
    .querySelectorAll(
      '.global-error-modal__backdrop, .global-error-modal__close, [data-modal-action]'
    )
    .forEach(el => el && el.addEventListener('click', close));

  modalWrapper.appendChild(clone);
  document.body.appendChild(modalWrapper);
}

// === ВІДПРАВКА ФОРМИ ===
if (buttonSubmit) {
  if (!buttonSubmit.dataset.listenerAttached) {
    buttonSubmit.addEventListener('click', handlerSubmit);
    buttonSubmit.dataset.listenerAttached = 'true';
  }
}

async function handlerSubmit() {
  const nameInput = form.querySelector('[name="customer-name"]');
  const name = nameInput.value.trim();
  if (name === '') {
    notify('form_complete', 'error');
    changeBorderColor(nameInput, '2px solid red');
    return;
  }

  const emailInput = form.querySelector('[name="customer-email"]');
  const email = emailInput.value.trim();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  if (!emailPattern.test(email)) {
    notify('invalid_email', 'error');
    changeBorderColor(emailInput, '2px solid red');
    return;
  }

  const phoneInput = form.querySelector('[name="customer-phone"]');
  const phone = phoneInput.value.trim();

  if (!isValidPhone(phone)) {
    notify('invalid_phone', 'error');
    changeBorderColor(phoneInput, '2px solid red');
    return;
  }

  if (!orderData.infoForDriver.confirmedPayment) {
    notify('confirm_payment', 'error');
    if (confirmPaymentCheckbox) {
      confirmPaymentCheckbox.style.outline = '2px solid red';
    }
    return;
  }

  if (!orderData.customer._id) {
    await API_createCustomer({
      name,
      email,
      phone,
    }).then(customer => {
      if (customer) {
        setCustomerData({ data: { ...customer } });
        setCustomerOrderData({ ...customer });
      }
    });
  }

  // =====================================
  // Активуємо фулскрін лоадер і блокуємо дії
  // =====================================
  const lang = document.documentElement.lang || 'en';
  const creatingText = common[lang]?.creating_order || common.en.creating_order;

  buttonSubmit.disabled = true;
  showRedirectLoader(creatingText);
  const loaderStartedAt = Date.now();

  try {
    // =====================================
    // 1. Створюємо замовлення на бекенді
    // =====================================
    const { order: _order, emailParams } = await API_createOrder(orderData);

    console.log('email-params ', emailParams); // ?????

    // =====================================
    // 2. Відправляємо емейл з деталями замовлення
    // =====================================

    const SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

    await emailjs.send(SERVICE_ID, TEMPLATE_ID, emailParams);
    notify('form_sent', 'success');

    // =====================================
    // 3. Очищаємо форми після успішної відправки
    // =====================================
    resetTripData();
    resetReturnTripData();
    resetSelectedCars();
    resetInfoForDriver();

    // =====================================
    // 4. Редірект на thank-you сторінку після успішного завершення
    // =====================================
    const elapsed = Date.now() - loaderStartedAt;
    if (elapsed < MIN_LOADER_DURATION) {
      await wait(MIN_LOADER_DURATION - elapsed);
    }
    redirectToThankYou();
  } catch (error) {
    console.error('❌ Order processing error:', error);

    // =====================================
    // Відновлюємо кнопку при помилці
    // =====================================
    const elapsed = Date.now() - loaderStartedAt;
    if (elapsed < MIN_LOADER_DURATION) {
      await wait(MIN_LOADER_DURATION - elapsed);
    }
    hideRedirectLoader();
    buttonSubmit.disabled = false;

    const errorMessage =
      error?.text ||
      error?.message ||
      error?.status ||
      'Something went wrong. Please try again.';

    showErrorModal(errorMessage);

    // Якщо помилка в EmailJS
    if (error.status || error.text) {
      console.error('❌ Email send error:', JSON.stringify(error, null, 2));
      const errorMessage = error.text || error.message || 'Email send failed';
      notify(`Email error: ${errorMessage}`, 'error');
    } else if (error.message) {
      // Якщо помилка в API з повідомленням
      console.error('❌ API error:', error.message);
      notify(`Order error: ${error.message}`, 'error');
    } else {
      // Загальна помилка без конкретного повідомлення
      notify('An unexpected error occurred. Please try again.', 'error');
    }
  }
}
